import { useCallback, useSyncExternalStore } from 'react';

const changed = 'ths:persistent-request-key';
const isKey = (value: string | null) =>
  value &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );

/** Persist only an opaque request identifier, under a caller-owned scope. */
export function usePersistentRequestKey(scope: string | null) {
  const storageKey = scope ? `ths:request:${scope}` : null;
  const read = useCallback(() => {
    if (!storageKey) return null;
    try {
      const value = localStorage.getItem(storageKey);
      return isKey(value) ? value : null;
    } catch {
      return null;
    }
  }, [storageKey]);
  const subscribe = useCallback((notify: () => void) => {
    window.addEventListener('storage', notify);
    window.addEventListener(changed, notify);
    return () => {
      window.removeEventListener('storage', notify);
      window.removeEventListener(changed, notify);
    };
  }, []);
  const requestKey = useSyncExternalStore(subscribe, read, () => null);
  const withLock = useCallback(
    async <T>(work: () => T): Promise<T> => {
      if (!storageKey) throw new Error('A request scope is required.');
      if (!navigator.locks)
        throw new Error(
          'This browser cannot safely retain a booking request. Use an up-to-date browser.',
        );
      return navigator.locks.request(storageKey, work);
    },
    [storageKey],
  );
  const write = useCallback(
    (key: string | null) => {
      if (!storageKey) throw new Error('A request scope is required.');
      if (key && !isKey(key))
        throw new Error('A valid request identifier is required.');
      if (key) localStorage.setItem(storageKey, key);
      else localStorage.removeItem(storageKey);
      window.dispatchEvent(new Event(changed));
    },
    [storageKey],
  );
  const replace = useCallback(
    (expected: string | null, key: string | null) =>
      withLock(() => {
        if (read() !== expected) return false;
        write(key);
        return true;
      }),
    [withLock, read, write],
  );
  const remember = useCallback((key: string) => replace(null, key), [replace]);
  const begin = useCallback(
    () =>
      withLock(() => {
        if (read())
          throw new Error(
            'Check the previous request before submitting again.',
          );
        const key = crypto.randomUUID();
        write(key);
        return key;
      }),
    [withLock, read, write],
  );
  return { requestKey, remember, replace, begin };
}
